import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface Cta {
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  variant?: "primary" | "secondary";
  icon?: ReactNode;
}

interface PageHeroProps {
  eyebrow?: string;
  /** First half of the headline */
  title: string;
  /** Optional gradient-highlighted word/phrase rendered after `title` */
  accent?: string;
  subtitle?: ReactNode;
  /** Center the copy column. Forced false when `rightSlot` is given. */
  centered?: boolean;
  ctas?: Cta[];
  rightSlot?: ReactNode;
  className?: string;
}

const ctaClass = (variant: "primary" | "secondary") =>
  variant === "primary"
    ? "h-12 px-7 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2 group bg-gradient-to-r from-primary to-primary-light text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 transition-all"
    : "h-12 px-6 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2 bg-surface border border-border text-foreground hover:bg-surface-muted transition-colors shadow-card";

const renderCta = (cta: Cta, idx: number) => {
  const v = cta.variant ?? (idx === 0 ? "primary" : "secondary");
  const cls = ctaClass(v);
  const inner = (
    <>
      {cta.icon}
      <span>{cta.label}</span>
      {v === "primary" && !cta.icon && (
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      )}
    </>
  );
  if (cta.to) return <Link key={idx} to={cta.to} className={cls}>{inner}</Link>;
  if (cta.href) return <a key={idx} href={cta.href} className={cls}>{inner}</a>;
  return <button key={idx} onClick={cta.onClick} className={cls}>{inner}</button>;
};

/** Dark glass card used inside `PageHero`'s rightSlot. */
export const HeroGlassCard = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className="relative">
    <div
      aria-hidden
      className="absolute -inset-8 -z-10 opacity-60"
      style={{
        background:
          "radial-gradient(60% 60% at 60% 40%, hsl(var(--primary) / 0.18), transparent 70%)",
      }}
    />
    <div
      className={cn(
        "relative rounded-2xl bg-gradient-to-br from-[hsl(220_60%_10%)] to-[hsl(220_55%_14%)] border border-white/10 shadow-2xl shadow-primary/30 p-5 md:p-6",
        className
      )}
    >
      {children}
    </div>
  </div>
);

export const PageHero = ({
  eyebrow,
  title,
  accent,
  subtitle,
  centered = false,
  ctas,
  rightSlot,
  className,
}: PageHeroProps) => {
  const hasRight = !!rightSlot;
  const isCentered = centered && !hasRight;

  return (
    <section className={cn("relative pt-6 md:pt-16 pb-10 md:pb-20", className)}>
      <div
        className={cn(
          "grid gap-8 lg:gap-10 items-center animate-fade-in",
          hasRight && "lg:grid-cols-12",
          isCentered && "text-center"
        )}
      >
        <div
          className={cn(
            hasRight ? "lg:col-span-6" : isCentered ? "max-w-3xl mx-auto" : "max-w-3xl"
          )}
        >
          {eyebrow && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] md:text-xs font-semibold tracking-widest uppercase mb-4 md:mb-6">
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span>{eyebrow}</span>
            </div>
          )}

          <h1 className="font-bold leading-[1.08] md:leading-[1.05] tracking-tight mb-4 md:mb-6 text-[clamp(2rem,8vw,4.5rem)] text-foreground text-balance">
            {title}
            {accent && (
              <>
                {" "}
                <span className="bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
                  {accent}
                </span>
              </>
            )}
          </h1>

          {subtitle && (
            <p
              className={cn(
                "text-[15px] md:text-lg text-muted-foreground mb-6 md:mb-8 leading-relaxed",
                isCentered ? "max-w-2xl mx-auto" : "max-w-xl"
              )}
            >
              {subtitle}
            </p>
          )}

          {ctas && ctas.length > 0 && (
            <div
              className={cn(
                "flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3",
                isCentered && "sm:justify-center"
              )}
            >
              {ctas.map(renderCta)}
            </div>
          )}
        </div>

        {hasRight && <div className="lg:col-span-6 relative">{rightSlot}</div>}
      </div>
    </section>
  );
};

export default PageHero;
